import DashboardService from "@/src/api/DashboardService.js";
import { getSourceOptionsFromAttribute } from "@/src/utils/schemaUtils.js";
import TicketsService from "@/src/api/TicketsService.js";
import store from "@/src/store/index.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CRMService from "@/src/api/CRMService.js";
import OutreachService from "@/src/api/OutreachService.js";
import {
  formComponentGroups,
  excludedFormFields,
  excludedAddFormFields,
  excludedCreateOnlyFields,
  customObjectAttributeTypes,
  ATTRIBUTE_TYPES,
  FIELDS_TO_CONVERT_EMPTY_TO_NULL,
  ENTITY_SLUG_TO_TYPE_MAP,
  isUUID,
  PIPELINE_FIELD_IDS,
  isFileLikeType,
} from "@/src/constants/crmConstants.js";
import { attributeTypes } from "@/src/constants/formConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { capitalizeFirstLetter, isDomainValid } from "@/src/utils/utils.js";
import { extractPhoneNumber } from "@/src/utils/dynamicCRMViewUtils.js";
import { fetchCompanyOptions as fetchCompanyOptionsUtil } from "@/src/utils/companyUtils.js";
import {
  getPrimaryAttributeSlug,
  getDisplayValueFromPrimaryAttribute,
} from "@/src/utils/entityUtils.js";
import { message } from "ant-design-vue";
import { COUNTRIES } from "@/src/constants/countries.js";
dayjs.extend(utc);

const normalizeTagOption = (tag) => {
  if (typeof tag === "object" && (tag.value || tag.label)) {
    return {
      label: tag.label || tag.value,
      value: tag.value || tag.label,
    };
  }
  return { label: tag, value: tag };
};

export const fetchTagsForEntity = async (entitySlug) => {
  if (!entitySlug) {
    return [];
  }

  const entityType = ENTITY_SLUG_TO_TYPE_MAP[entitySlug.toLowerCase()];

  if (!entityType) {
    console.warn(`No entity type mapping found for slug: ${entitySlug}`);
    return [];
  }

  try {
    const methodName = `getTagsFor${entityType}`;
    const response = await DashboardService[methodName]();

    if (response?.success && Array.isArray(response.tags)) {
      return response.tags
        .filter((tag) => tag !== null && tag !== undefined && tag !== "")
        .map(normalizeTagOption);
    }

    return [];
  } catch (error) {
    console.error(`Error fetching tags for ${entityType}:`, error);
    return [];
  }
};

export const fetchCountryOptions = async () => {
  return COUNTRIES.map(({ name }) => ({ label: name, value: name }));
};

/**
 * Fetches pipeline options from the Vuex pipeline store.
 * Returns options with label (slug) and value (id).
 */
export const fetchPipelineOptions = async () => {
  await store.dispatch("pipelines/fetchPipelines");
  const pipelines = store.getters["pipelines/allPipelines"];
  return pipelines.map((p) => ({ label: p.slug || p.name, value: p.id }));
};

/**
 * Fetches all pipeline stage options across all pipelines.
 * Uses the fetchPipelineStages action which calls the /api/pipelines_stages endpoint.
 * Returns options with label (slug) and value (id).
 */
export const fetchAllPipelineStageOptions = async () => {
  const response = await store.dispatch("pipelines/fetchPipelineStages", {});
  if (response?.success && response?.pipeline_stages) {
    return response.pipeline_stages.map((stage) => ({
      label: stage.slug || stage.name,
      value: stage.id,
    }));
  }
  return [];
};

/**
 * Fetches pipeline stage options filtered by a specific pipeline ID.
 * Returns options with label (slug) and value (id).
 */
export const fetchPipelineStageOptionsByPipelineId = async (pipelineId) => {
  const response = await store.dispatch("pipelines/fetchPipelineStages", {
    pipelineId,
  });
  if (response?.success && response?.pipeline_stages) {
    return response.pipeline_stages.map((stage) => ({
      label: stage.slug || stage.name,
      value: stage.id,
    }));
  }
  return [];
};

export const fetchSystemPropertyOptions = async (
  fieldKey,
  entitySlug,
  searchTerm = "",
) => {
  try {
    const fieldKeyLower = String(fieldKey || "").toLowerCase();

    // Industry uses OutreachService.campaignFilters for search
    if (fieldKeyLower === "industry") {
      const response = await OutreachService.campaignFilters(
        "industry",
        searchTerm,
      );
      if (response?.success) {
        return (
          response?.options?.map((option) => ({
            label: option,
            value: option,
          })) || []
        );
      }
      return [];
    }

    // Pipeline fields use the Vuex pipeline store for cached data
    const pipelineFields = ["pipeline", "pipeline_id"];
    const pipelineStageFields = [
      "pipeline_stage_id",
      "pipeline_stage",
      "deal_stage",
      "deal_stage_id",
    ];

    if (pipelineFields.includes(fieldKeyLower)) {
      return await fetchPipelineOptions();
    }

    if (pipelineStageFields.includes(fieldKeyLower)) {
      return await fetchAllPipelineStageOptions();
    }

    if (!entitySlug) {
      return [];
    }

    // Derive singular entity type from object slug (e.g. "deals" → "deal", "companies" → "company")
    const entityType = entitySlug.endsWith("ies")
      ? entitySlug.slice(0, -3) + "y"
      : entitySlug.endsWith("s")
        ? entitySlug.slice(0, -1)
        : entitySlug;

    const response = await DashboardService.fetchObjectPropertyStatus(
      entityType,
      fieldKeyLower,
    );

    if (response?.success && response?.property_details?.options) {
      const options = response.property_details.options.map((option) => {
        // Handle options that are objects with id and name
        if (
          typeof option === "object" &&
          option !== null &&
          "id" in option &&
          "name" in option
        ) {
          return {
            label: option.name,
            value: option.id,
          };
        }
        // Handle simple string options
        return {
          label: option,
          value: option,
        };
      });
      return options;
    }

    console.warn("[fetchSystemPropertyOptions] No options in response");
    return [];
  } catch (error) {
    console.error(
      "[fetchSystemPropertyOptions] Error fetching system property options:",
      error,
    );
    return [];
  }
};

export const fetchCompanyOptions = async (params = {}) => {
  try {
    const searchTerm = params.searchQuery || "";
    const options = await fetchCompanyOptionsUtil({
      searchTerm,
      limit: 100,
    });
    return options;
  } catch (error) {
    console.error("Error fetching company options:", error);
    return [];
  }
};

export const fetchUserOptions = async (params = {}) => {
  const { status = "all", entityType = "" } = params;

  const mapUser = (user) => ({
    id: user.id,
    value: user.id,
    label: user.name || user.email,
    name: user.name,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    profile_pic: user.profile_pic,
    logo_url: user.logo_url,
    avatar_color: user.avatar_color,
    data: user,
    ...(user.isDeleted ? { isDeleted: true, disabled: true } : {}),
  });

  try {
    let storeUsers = null;
    let store = null;

    try {
      const storeModule = await import("@/src/store/index.js");
      store = storeModule.default;

      // Use usersWithDeleted to include deleted users (shown as disabled)
      storeUsers = store?.getters?.["users/usersWithDeleted"];

      if (storeUsers && Array.isArray(storeUsers) && storeUsers.length > 0) {
        return storeUsers.map(mapUser);
      }

      // If store exists but users are not loaded, dispatch the action to fetch them
      if (store && (!storeUsers || storeUsers.length === 0)) {
        console.debug(
          "Users not in store, dispatching fetchUsersWithDeleted action",
        );
        await store.dispatch("users/fetchUsersWithDeleted");
        storeUsers = store?.getters?.["users/usersWithDeleted"];

        if (storeUsers && Array.isArray(storeUsers) && storeUsers.length > 0) {
          return storeUsers.map(mapUser);
        }
      }
    } catch (storeError) {
      console.debug(
        "Store not available, fetching from API directly",
        storeError,
      );
    }

    // Fallback to direct API call if store is not available
    const response = await DashboardService.fetchUsers(status);

    if (response?.success && response?.users) {
      return response.users.map(mapUser);
    } else {
      console.warn(
        "Failed to fetch users:",
        response?.message || "Unknown error",
      );
      return [];
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    message.error("Failed to load users");
    return [];
  }
};

export const getSupportedAttributeTypes = () => {
  return customObjectAttributeTypes.map((type) => type.value);
};

export const isSupportedAttributeType = (type) => {
  if (!type) return false;
  const supportedTypes = getSupportedAttributeTypes();
  return supportedTypes.includes(String(type).toLowerCase());
};

export const getAttributeTypeInfo = (type) => {
  if (!type) return null;
  const normalizedType = String(type).toLowerCase();
  return (
    customObjectAttributeTypes.find(
      (attributeType) => attributeType.value.toLowerCase() === normalizedType,
    ) || null
  );
};

export const getComponentTypeFromDatatype = (
  type,
  fieldSlug = null,
  fieldId = null,
) => {
  if (!type) return "input";

  const normalizedType = String(type).toLowerCase();
  const normalizedSlug = String(fieldSlug || "").toLowerCase();
  const normalizedId = String(fieldId || "").toLowerCase();

  // Override component type for specific fields that should always be select dropdowns
  const forceSelectFields = [
    "company_size",
    "priority",
    "deal_type",
    "pipeline",
    "deal_stage",
    "pipeline_id",
    "pipeline_stage_id",
    "pipeline_stage",
    "deal_stage_id",
    "country",
    "countries",
  ];
  // Check both id and slug for standard fields
  if (
    forceSelectFields.includes(normalizedId) ||
    forceSelectFields.includes(normalizedSlug)
  ) {
    return "select";
  }

  const componentTypeMap = {
    [ATTRIBUTE_TYPES.TEXT]: "input",
    [ATTRIBUTE_TYPES.EMAIL]: "email",
    [ATTRIBUTE_TYPES.URL]: "url",
    [ATTRIBUTE_TYPES.PHONE_NUMBER]: "phone_number",
    [ATTRIBUTE_TYPES.NUMBER]: "number",
    [ATTRIBUTE_TYPES.DATE]: "date",
    [ATTRIBUTE_TYPES.DATETIME]: "datetime",
    [ATTRIBUTE_TYPES.TIME]: "time",
    [ATTRIBUTE_TYPES.BOOLEAN]: "boolean",
    [ATTRIBUTE_TYPES.SELECT]: "select",
    [ATTRIBUTE_TYPES.MULTI_SELECT]: "multiselect",
    [ATTRIBUTE_TYPES.USER]: "user",
    [ATTRIBUTE_TYPES.TEXTAREA]: "textarea",
    [ATTRIBUTE_TYPES.FILE]: "file",
    [ATTRIBUTE_TYPES.IMAGE]: "file",
    [ATTRIBUTE_TYPES.TAGS]: "tags",
    relationship: "relationship",
  };

  if (componentTypeMap.hasOwnProperty(normalizedType)) {
    return componentTypeMap[normalizedType];
  }

  return "input";
};

export const getPlaceholderText = (property, componentType) => {
  const name = property.name || property.label || "value";

  if (
    formComponentGroups.selectLike.includes(componentType) ||
    formComponentGroups.booleanLike.includes(componentType) ||
    formComponentGroups.dateLike.includes(componentType)
  ) {
    return `Select ${name}`;
  }

  if (
    formComponentGroups.textLike.includes(componentType) ||
    formComponentGroups.numberLike.includes(componentType) ||
    formComponentGroups.phoneLike.includes(componentType)
  ) {
    return `Enter ${name}`;
  }

  return `Enter ${name}`;
};

export const validateFieldValue = (value, property, componentType) => {
  const fieldName = property.name || property.label || "Field";

  if (property.required || property.is_required) {
    if (value === null || value === undefined || value === "") {
      return { isValid: false, error: `${fieldName} is required!` };
    }
    if (Array.isArray(value) && value.length === 0) {
      return { isValid: false, error: `${fieldName} is required!` };
    }
  }

  if (value !== null && value !== undefined && value !== "") {
    switch (componentType) {
      case ATTRIBUTE_TYPES.EMAIL: {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return {
            isValid: false,
            error: "Please enter a valid email address",
          };
        }
        break;
      }
      case ATTRIBUTE_TYPES.URL: {
        try {
          new URL(value.startsWith("http") ? value : `https://${value}`);
        } catch {
          return { isValid: false, error: "Please enter a valid URL" };
        }
        break;
      }
      case ATTRIBUTE_TYPES.PHONE_NUMBER: {
        const phoneRegex = /^[\+]?[^\D][\d]{0,15}$/;
        if (!phoneRegex.test(String(value).replace(/[\s\-\(\)]/g, ""))) {
          return { isValid: false, error: "Please enter a valid phone number" };
        }
        break;
      }
      case ATTRIBUTE_TYPES.NUMBER: {
        if (isNaN(Number(value))) {
          return { isValid: false, error: "Please enter a valid number" };
        }
        break;
      }
    }
  }

  return { isValid: true, error: null };
};

export const formatDisplayValue = (value, componentType) => {
  if (value === null || value === undefined) return "";
  switch (componentType) {
    case ATTRIBUTE_TYPES.BOOLEAN:
      return Boolean(value);
    case "multiselect":
    case "relationship":
      return Array.isArray(value) ? value : [];
    default:
      return value;
  }
};

const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return "";
  return phone.replace(/[\s\-\(\)\.]/g, "");
};

export const transformFormDataForAPI = (
  formData,
  properties,
  originalData = null,
  options = {},
) => {
  const attributes = {};
  const relations = {};

  properties.forEach((property) => {
    const fieldKey = property.slug || property.name;
    if (excludedFormFields.includes(fieldKey)) return;
    const fieldType = property.type || property.datatype;
    const fieldTypeLower = String(fieldType || "").toLowerCase();

    if (fieldTypeLower === "formula") return;
    if (fieldTypeLower === "select_timestamp") return;

    const componentType = getComponentTypeFromDatatype(
      fieldType,
      fieldKey,
      property.id,
    );
    const value = formData[fieldKey];

    const isEmpty = (val) => {
      if (val === null || val === undefined) return true;
      if (typeof val === "string" && val.trim() === "") return true;
      if (Array.isArray(val) && val.length === 0) return true;
      return false;
    };

    const isCreating = originalData === null;
    const shouldIncludeField = isCreating ? !isEmpty(value) : true;

    if (fieldTypeLower === "relationship") {
      // Extract IDs from labelInValue format { value, label } or plain IDs
      const extractId = (v) => {
        if (v === null || v === undefined) return null;
        if (typeof v === "object" && v !== null) return v.value ?? v.id ?? null;
        return v;
      };

      const newIds = Array.isArray(value)
        ? value.map(extractId).filter((v) => v !== null && v !== undefined)
        : value
          ? [extractId(value)].filter((v) => v !== null && v !== undefined)
          : [];

      const originalIds = Array.isArray(originalData?.[fieldKey])
        ? originalData[fieldKey]
            .map(extractId)
            .filter((v) => v !== null && v !== undefined)
        : [];

      const idsChanged = JSON.stringify(newIds) !== JSON.stringify(originalIds);

      if ((isCreating && newIds.length > 0) || (!isCreating && idsChanged)) {
        // For virtual relationships (dynamic objects), include relationship_id and target_object_type_id
        // so the backend can create relationships via the relationship service
        if (property._relationshipId) {
          relations[fieldKey] = {
            ids: newIds,
            relationship_id: property._relationshipId,
            target_object_type_id: property.slug || fieldKey,
          };
        } else {
          // Legacy format for tickets and other objects
          relations[fieldKey] = { ids: newIds };
        }
      }

      return;
    }

    if (fieldTypeLower === ATTRIBUTE_TYPES.RECORD) {
      const rawRecords = Array.isArray(value)
        ? value
        : Array.isArray(value?.records)
          ? value.records
          : [];
      const normalizedRecords = rawRecords.map((record) => ({
        object_type_id: record.object_type_id,
        record_id: record.record_id,
        record_display_name: record.record_display_name || "",
      }));

      const originalRecords = Array.isArray(originalData?.[fieldKey])
        ? originalData[fieldKey]
        : Array.isArray(originalData?.[fieldKey]?.records)
          ? originalData[fieldKey].records
          : [];

      const normalizedForCompare = normalizedRecords.map((r) => ({
        object_type_id: r.object_type_id,
        record_id: r.record_id,
      }));
      const originalForCompare = originalRecords.map((r) => ({
        object_type_id: r.object_type_id,
        record_id: r.record_id,
      }));

      const recordsChanged =
        JSON.stringify(normalizedForCompare) !==
        JSON.stringify(originalForCompare);

      if (
        (isCreating && normalizedRecords.length > 0) ||
        (!isCreating && recordsChanged)
      ) {
        attributes[fieldKey] = { records: normalizedRecords };
      }

      return;
    }

    if (!isCreating && value === undefined) {
      const fieldWasInFormData = Object.prototype.hasOwnProperty.call(
        formData,
        fieldKey,
      );
      if (!fieldWasInFormData) {
        return;
      }

      const originalValue = originalData ? originalData[fieldKey] : undefined;
      const originalHadValue =
        originalValue !== null &&
        originalValue !== undefined &&
        originalValue !== "";
      if (originalHadValue) {
        // Value was cleared - send null to clear it on the server
        attributes[fieldKey] = null;
      }
      return;
    }

    if (shouldIncludeField && value !== undefined) {
      let transformedValue;
      switch (componentType) {
        case ATTRIBUTE_TYPES.NUMBER:
          transformedValue = value !== "" ? Number(value) : null;
          break;
        case ATTRIBUTE_TYPES.BOOLEAN:
          if (value === null || value === undefined) {
            transformedValue = value;
          } else {
            transformedValue = Boolean(value);
          }
          break;
        case "multiselect":
          transformedValue = Array.isArray(value)
            ? value.filter((v) => v !== null && v !== undefined)
            : [];
          break;
        case ATTRIBUTE_TYPES.DATE: {
          if (dayjs.isDayjs(value)) {
            transformedValue = value.format("YYYY-MM-DD");
          } else if (typeof value === "string") {
            const d = dayjs(value);
            transformedValue = d.isValid() ? d.format("YYYY-MM-DD") : value;
          } else {
            transformedValue = value;
          }
          break;
        }
        case ATTRIBUTE_TYPES.DATETIME: {
          if (dayjs.isDayjs(value)) {
            transformedValue = value.utc().format("YYYY-MM-DD HH:mm:ss");
          } else if (typeof value === "string") {
            const d = dayjs(value);
            transformedValue = d.isValid()
              ? d.utc().format("YYYY-MM-DD HH:mm:ss")
              : value;
          } else {
            transformedValue = value;
          }
          break;
        }
        case ATTRIBUTE_TYPES.TIME: {
          if (dayjs.isDayjs(value)) {
            transformedValue = value.format("HH:mm");
          } else {
            transformedValue = value;
          }
          break;
        }
        default:
          transformedValue = value;
      }

      if (!isCreating) {
        const shouldConvertToNull =
          FIELDS_TO_CONVERT_EMPTY_TO_NULL.includes(fieldTypeLower);

        if (
          shouldConvertToNull &&
          typeof transformedValue === "string" &&
          transformedValue.trim() === ""
        ) {
          transformedValue = null;
        }
      }

      const originalValue = originalData ? originalData[fieldKey] : undefined;
      const isEqual = (() => {
        if (Array.isArray(transformedValue)) {
          // Treat undefined/null as equivalent to empty array
          const normalizedOriginal = Array.isArray(originalValue)
            ? originalValue
            : [];
          // Both empty arrays should be considered equal
          if (
            transformedValue.length === 0 &&
            normalizedOriginal.length === 0
          ) {
            return true;
          }
          return (
            JSON.stringify(transformedValue) ===
            JSON.stringify(normalizedOriginal)
          );
        }

        const isTransformedEmpty =
          transformedValue === null ||
          transformedValue === undefined ||
          transformedValue === "";
        const isOriginalEmpty =
          originalValue === null ||
          originalValue === undefined ||
          originalValue === "";

        if (isTransformedEmpty && isOriginalEmpty) {
          return true;
        }

        if (componentType === ATTRIBUTE_TYPES.BOOLEAN) {
          if (
            (originalValue === null || originalValue === undefined) &&
            transformedValue === false &&
            !options.allowExplicitFalse
          ) {
            return true;
          }
        }

        if (componentType === ATTRIBUTE_TYPES.NUMBER) {
          if (
            (originalValue === null || originalValue === undefined) &&
            transformedValue === 0
          ) {
            return true;
          }
          // Compare numbers by converting both to Number to handle string/number type mismatches
          // e.g., "1000099.0" (string from API) vs 1000099 (number in form)
          const numTransformed = Number(transformedValue);
          const numOriginal = Number(originalValue);
          if (!isNaN(numTransformed) && !isNaN(numOriginal)) {
            return numTransformed === numOriginal;
          }
        }

        if (
          componentType === ATTRIBUTE_TYPES.DATE ||
          componentType === ATTRIBUTE_TYPES.DATETIME
        ) {
          if (dayjs.isDayjs(originalValue)) {
            const originalFormatted =
              componentType === ATTRIBUTE_TYPES.DATETIME
                ? originalValue.utc().format("YYYY-MM-DD HH:mm:ss")
                : originalValue.format("YYYY-MM-DD");
            return transformedValue === originalFormatted;
          }

          if (typeof originalValue === "number") {
            const normalizedTs = normalizeTimestamp(originalValue);
            if (normalizedTs !== null) {
              const originalDate = dayjs(normalizedTs);
              if (originalDate.isValid()) {
                const originalFormatted =
                  componentType === ATTRIBUTE_TYPES.DATETIME
                    ? originalDate.utc().format("YYYY-MM-DD HH:mm:ss")
                    : originalDate.format("YYYY-MM-DD");
                return transformedValue === originalFormatted;
              }
            }
          }

          if (typeof originalValue === "string" && originalValue) {
            const originalDate = dayjs(originalValue);
            if (originalDate.isValid()) {
              const originalFormatted =
                componentType === ATTRIBUTE_TYPES.DATETIME
                  ? originalDate.utc().format("YYYY-MM-DD HH:mm:ss")
                  : originalDate.format("YYYY-MM-DD");
              return transformedValue === originalFormatted;
            }
          }
        }

        const fieldKeyLower = String(fieldKey || "").toLowerCase();
        if (
          fieldTypeLower === ATTRIBUTE_TYPES.PHONE_NUMBER ||
          fieldKeyLower.includes("phone")
        ) {
          const normalizedTransformed = normalizePhoneNumber(transformedValue);
          const normalizedOriginal = normalizePhoneNumber(originalValue);
          return normalizedTransformed === normalizedOriginal;
        }

        // For objects (like current_company), compare by JSON stringification
        if (
          typeof transformedValue === "object" &&
          transformedValue !== null &&
          typeof originalValue === "object" &&
          originalValue !== null
        ) {
          return (
            JSON.stringify(transformedValue) === JSON.stringify(originalValue)
          );
        }

        return transformedValue === originalValue;
      })();

      if (isCreating) {
        if (!isEmpty(transformedValue)) {
          attributes[fieldKey] = transformedValue;
        }
      } else if (!isEqual) {
        attributes[fieldKey] = transformedValue;
      }
    }
  });

  return { attributes, relations };
};

export const fetchFieldOptions = async (property, params = {}) => {
  const fieldType = property.type || property.datatype;
  const fieldKey = property.slug || property.name;
  const fieldId = property.id;
  try {
    const typeLower = String(fieldType || "").toLowerCase();
    const idLower = String(fieldId || "").toLowerCase();
    const slugLower = String(fieldKey || "").toLowerCase();

    if (typeLower === "relationship") {
      const currentSlug = params.entitySlug || "";
      const sourceSlug =
        property?.metadata?.source_object?.slug ||
        property?.metadata?.source_object_id ||
        "";
      const targetSlug =
        property?.metadata?.target_object?.slug ||
        property?.metadata?.target_object_id ||
        "";

      const fetchFromSlug =
        currentSlug === targetSlug ? sourceSlug : targetSlug;

      if (!fetchFromSlug) return [];

      const fetchFromLower = String(fetchFromSlug).toLowerCase();
      const searchQuery = params.searchQuery || "";
      const hasSearchQuery = searchQuery && searchQuery.trim() !== "";

      const standardObject = standardObjects.find(
        (obj) => obj.slug === fetchFromLower,
      );
      const isTickets = fetchFromLower === entityTypes.TICKETS;
      const isTasks = fetchFromLower === entityTypes.TASKS;

      const buildOptions = (list = [], primaryAttrSlug = null) => {
        // Try primary attribute slug first (handles renamed properties)
        const extractByPrimaryAttr = (rec) => {
          if (!primaryAttrSlug) return null;
          // Check flat format (standard objects): rec[slug]
          const flatVal = rec?.[primaryAttrSlug];
          if (flatVal && typeof flatVal === "string" && flatVal.trim())
            return flatVal;
          // Check nested format (custom/dynamic objects): rec.attributes[slug]
          const attrs = rec?.attributes || {};
          const displayValue = getDisplayValueFromPrimaryAttribute(
            attrs,
            primaryAttrSlug,
            null,
          );
          return displayValue || null;
        };

        const labelExtractors = {
          contacts: (rec) => {
            const attrs = rec?.attributes || {};
            const name = attrs?.full_name?.value || attrs?.name?.value || rec?.full_name || rec?.name;
            const email = attrs?.email?.value || rec?.email;
            return name && name.trim() ? name : email || String(rec?.id);
          },
          companies: (rec) => {
            const attrs = rec?.attributes || {};
            return attrs?.name?.value || rec?.company_name || rec?.name || String(rec?.id);
          },
          deals: (rec) => {
            const attrs = rec?.attributes || {};
            return attrs?.title?.value || attrs?.name?.value || rec?.title || rec?.name || String(rec?.id);
          },
          leads: (rec) => {
            const attrs = rec?.attributes || {};
            const name = attrs?.full_name?.value || attrs?.name?.value || rec?.full_name || rec?.name;
            const email = attrs?.email?.value || rec?.email;
            return name && name.trim() ? name : email || String(rec?.id);
          },
          tickets: (rec) => rec?.attributes?.summary?.value || "",
          tasks: (rec) => {
            const attrs = rec?.attributes || {};
            return attrs?.name?.value || attrs?.title?.value || String(rec?.id);
          },
          default: (rec) => {
            const attrs = rec?.attributes || {};
            return attrs?.name?.value || attrs?.title?.value || String(rec?.id);
          },
        };

        const fallbackExtractor =
          labelExtractors[fetchFromLower] || labelExtractors.default;

        return (list || []).map((rec) => ({
          label:
            extractByPrimaryAttr(rec) ||
            fallbackExtractor(rec) ||
            String(rec?.id),
          value: rec.id,
        }));
      };

      const getViewId = async (slug, svc = null) => {
        const viewData = await CRMService.fetchDynamicEntityViews(
          slug,
          null,
          svc,
        );
        return viewData?.success && viewData?.currentView?.id
          ? viewData.currentView.id
          : 1;
      };

      const signal = params.signal;
      const axiosConfig = signal ? { signal } : {};

      // Fetch primary attribute slug for display name resolution (handles renamed properties)
      let primaryAttrSlug = null;
      if (!isTickets && !isTasks && fetchFromSlug) {
        try {
          primaryAttrSlug = await getPrimaryAttributeSlug(fetchFromSlug);
        } catch (error) {
          console.warn(
            `Failed to fetch primary attribute for ${fetchFromSlug}:`,
            error,
          );
        }
      }

      try {
        let response;
        let results = [];

        if (isTickets) {
          const viewId = await getViewId(entityTypes.TICKETS);
          const ticketParams = { page: 1, page_size: 20 };
          if (hasSearchQuery) ticketParams.search_query = searchQuery;

          response = await TicketsService.getViewResults(viewId, ticketParams);
          results = response?.success
            ? response.tickets || response.results || []
            : [];
        } else if (isTasks) {
          console.warn(
            "Task relationships require project context. Skipping fetch.",
          );
          return [];
        } else if (standardObject) {
          response = await CRMService.searchDynamicObjects(
            {
              object_type: fetchFromLower,
              search_query: hasSearchQuery ? searchQuery : "",
              page: 1,
              page_size: 20,
            },
            axiosConfig,
          );
          results = response?.success ? response.results || [] : [];
        } else {
          if (hasSearchQuery) {
            response = await CRMService.searchDynamicObjects(
              {
                object_type: fetchFromLower,
                search_query: searchQuery,
                page: 1,
                page_size: 20,
              },
              axiosConfig,
            );
            results = response?.success ? response.results || [] : [];
          } else {
            const viewId = await getViewId(fetchFromLower);
            response = await CRMService.fetchDynamicEntityData(
              fetchFromLower,
              viewId,
              1,
              20,
              undefined,
            );
            results = response?.success ? response.results || [] : [];
          }
        }

        return buildOptions(results, primaryAttrSlug);
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") {
          return [];
        }
        console.error("Error fetching relationship options:", error);
        return [];
      }
    }

    // Check for country fields regardless of type
    if (
      idLower === "country" ||
      idLower === "countries" ||
      slugLower === "country" ||
      slugLower === "countries"
    ) {
      const options = await fetchCountryOptions();
      return options;
    }

    if (typeLower === ATTRIBUTE_TYPES.USER) {
      const options = await fetchUserOptions(params);
      return options;
    }

    if (
      (typeLower === ATTRIBUTE_TYPES.SELECT ||
        typeLower === ATTRIBUTE_TYPES.MULTI_SELECT) &&
      (idLower === "current_company" ||
        idLower === "company" ||
        idLower === "current_company_id" ||
        slugLower === "current_company" ||
        slugLower === "company" ||
        slugLower === "current_company_id")
    ) {
      const options = await fetchCompanyOptions(params);
      return options;
    }

    // Pipeline/stage fields — use schema structured_options directly
    if (
      PIPELINE_FIELD_IDS.includes(idLower) ||
      PIPELINE_FIELD_IDS.includes(slugLower)
    ) {
      const raw =
        property.structured_options?.length > 0
          ? property.structured_options
          : property.options?.length > 0
            ? property.options
            : [];
      return toFormOptions(raw).map((opt) => ({
        ...opt,
        value: Number(opt.value) || opt.value,
      }));
    }

    // Handle tags fields - use schema options when available, else fetch from API
    if (typeLower === "tags" || idLower === "tags") {
      if (
        property.options &&
        Array.isArray(property.options) &&
        property.options.length > 0
      ) {
        return toFormOptions(property.options);
      }
      return await fetchTagsForEntity(params.entitySlug);
    }

    // Handle lead source field - fetch from schema only
    if (
      // Not slugLower to avoid conflicts with custom objects
      idLower === "source" &&
      params.entitySlug === entityTypes.LEADS
    ) {
      return getSourceOptionsFromAttribute(property).map((opt) => ({
        label: opt,
        value: opt,
      }));
    }

    if (
      (typeLower === ATTRIBUTE_TYPES.SELECT ||
        typeLower === ATTRIBUTE_TYPES.MULTI_SELECT) &&
      property.options &&
      Array.isArray(property.options) &&
      property.options.length > 0
    ) {
      return toFormOptions(property.options);
    }

    return [];
  } catch (error) {
    console.error(`Error fetching options for field ${fieldKey}:`, error);
    message.error(`Failed to load options for ${property.name || fieldKey}`);
    return [];
  }
};

export const toFormOptions = (options = []) => {
  return (options || []).map((opt) =>
    typeof opt === "string"
      ? { label: opt, value: opt }
      : {
          label: opt.label ?? opt.name ?? opt.id ?? "",
          value: opt.value ?? opt.id ?? opt.name ?? "",
          ...opt,
        },
  );
};

// Filter pipeline stage structured_options by selected pipeline.
// Stage labels follow the format "StageName (PipelineName)".
export const filterStagesByPipeline = (
  stageOptions,
  selectedPipelineId,
  pipelineOptions,
) => {
  if (!selectedPipelineId || !pipelineOptions?.length) return stageOptions;
  const pipeline = pipelineOptions.find(
    (p) => String(p.id) === String(selectedPipelineId),
  );
  if (!pipeline) return stageOptions;
  return stageOptions.filter((s) => s.label.endsWith(`(${pipeline.label})`));
};

export const getPrimaryNameFieldSlug = (attributes = []) => {
  if (!attributes || attributes.length === 0) return null;
  const nameAttr = attributes.find((attr) =>
    (attr.slug || "").includes("name"),
  );
  return nameAttr ? nameAttr.slug : null;
};

export const getAvatarFieldSlug = (attributes = []) => {
  if (!attributes || attributes.length === 0) return null;
  const avatarAttr = attributes.find(
    (attr) =>
      (attr.slug || "").includes("logo") ||
      (attr.slug || "").includes("image") ||
      (attr.slug || "").includes("avatar"),
  );
  return avatarAttr ? avatarAttr.slug : null;
};

export const getDisplayNameFromEntity = (entity = {}, attributes = []) => {
  const primaryNameSlug = getPrimaryNameFieldSlug(attributes);
  if (primaryNameSlug && entity && entity[primaryNameSlug])
    return entity[primaryNameSlug];
  return (
    entity?.name ||
    entity?.personal_name ||
    entity?.full_name ||
    entity?.company_name ||
    ""
  );
};

export const normalizeTimestamp = (timestamp) => {
  let numericTimestamp = timestamp;
  if (typeof timestamp === "string") {
    numericTimestamp = parseInt(timestamp, 10);
  }

  if (typeof numericTimestamp !== "number" || isNaN(numericTimestamp)) {
    return null;
  }

  const isInSeconds = numericTimestamp < 10000000000;
  return isInSeconds ? numericTimestamp * 1000 : numericTimestamp;
};

const extractValueByType = (extractedValue, effectiveType) => {
  if (Array.isArray(extractedValue)) {
    if (effectiveType === ATTRIBUTE_TYPES.PHONE_NUMBER) {
      return extractPhoneNumber(extractedValue[0]);
    } else if (effectiveType === ATTRIBUTE_TYPES.DOMAIN) {
      const first = extractedValue[0];
      return typeof first === "string" ? first : first?.domain || "";
    } else if (effectiveType === ATTRIBUTE_TYPES.EMAIL) {
      const first = extractedValue[0];
      return typeof first === "string" ? first : first?.email || "";
    }
    return extractedValue;
  }

  if (effectiveType === ATTRIBUTE_TYPES.BOOLEAN) {
    if (typeof extractedValue === "boolean") {
      return extractedValue;
    }
    if (typeof extractedValue === "string") {
      const lowerValue = extractedValue.toLowerCase();
      if (lowerValue === "true" || lowerValue === "yes") return true;
      if (lowerValue === "false" || lowerValue === "no") return false;
    }
    return extractedValue;
  }

  if (effectiveType === ATTRIBUTE_TYPES.NUMBER) {
    if (typeof extractedValue === "number") {
      return extractedValue;
    }
    if (typeof extractedValue === "string" && extractedValue.trim() !== "") {
      const num = Number(extractedValue);
      if (!isNaN(num)) return num;
    }
    return extractedValue;
  }

  if (
    effectiveType === ATTRIBUTE_TYPES.DATETIME &&
    typeof extractedValue === "number"
  ) {
    const normalizedTs = normalizeTimestamp(extractedValue);
    if (normalizedTs !== null) {
      const d = dayjs(normalizedTs);
      if (d.isValid()) {
        return d.format("YYYY-MM-DD HH:mm:ss");
      }
    }
  } else if (
    effectiveType === ATTRIBUTE_TYPES.DATE &&
    typeof extractedValue === "number"
  ) {
    const normalizedTs = normalizeTimestamp(extractedValue);
    if (normalizedTs !== null) {
      const d = dayjs(normalizedTs);
      if (d.isValid()) {
        return d.format("YYYY-MM-DD");
      }
    }
  } else if (
    (effectiveType === ATTRIBUTE_TYPES.MULTI_SELECT ||
      effectiveType === "tags") &&
    extractedValue === null
  ) {
    return [];
  }

  return extractedValue;
};

export const normalizeApiAttributes = (attributes = {}, properties = []) => {
  const result = {};
  if (!attributes || typeof attributes !== "object") return result;

  const propertiesBySlug = (properties || []).reduce((acc, p) => {
    acc[p.slug] = p;
    return acc;
  }, {});

  Object.keys(attributes).forEach((key) => {
    if (key === "id") return;
    const value = attributes[key];
    const property = propertiesBySlug[key];
    const fieldType = (
      property?.type ||
      property?.datatype ||
      ""
    ).toLowerCase();

    if (Array.isArray(value)) {
      if (fieldType === ATTRIBUTE_TYPES.PHONE_NUMBER) {
        result[key] = extractPhoneNumber(value[0]);
      } else if (fieldType === ATTRIBUTE_TYPES.DOMAIN) {
        const first = value[0];
        result[key] = typeof first === "string" ? first : first?.domain || "";
      } else if (fieldType === ATTRIBUTE_TYPES.EMAIL) {
        const first = value[0];
        result[key] = typeof first === "string" ? first : first?.email || "";
      } else {
        result[key] = value;
      }
    } else if (
      value &&
      typeof value === "object" &&
      Object.prototype.hasOwnProperty.call(value, "value")
    ) {
      let extractedValue = value.value;

      let effectiveType = fieldType;
      if (fieldType === ATTRIBUTE_TYPES.PROPERTY_SYNC && value.source_type) {
        effectiveType = value.source_type.toLowerCase();
      } else if (fieldType === ATTRIBUTE_TYPES.FORMULA && value.output_type) {
        effectiveType = value.output_type.toLowerCase();
      }

      // Special case: pipeline_stage_id may come as array, extract first value as number
      if (key === "pipeline_stage_id" && Array.isArray(extractedValue)) {
        const firstValue = extractedValue[0];
        extractedValue = firstValue ? Number(firstValue) : null;
      }

      result[key] = extractValueByType(extractedValue, effectiveType);
    } else if (
      fieldType === ATTRIBUTE_TYPES.DATETIME &&
      typeof value === "number"
    ) {
      const normalizedTs = normalizeTimestamp(value);
      if (normalizedTs !== null) {
        const d = dayjs(normalizedTs);
        if (d.isValid()) {
          result[key] = d.format("YYYY-MM-DD HH:mm:ss");
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    } else if (
      (fieldType === ATTRIBUTE_TYPES.MULTI_SELECT || fieldType === "tags") &&
      (value === null || value === "")
    ) {
      result[key] = [];
    } else if (
      isFileLikeType(fieldType) &&
      value &&
      typeof value === "object" &&
      value.files &&
      Array.isArray(value.files)
    ) {
      result[key] = value.files;
    } else if (
      fieldType === "select_timestamp" &&
      value &&
      typeof value === "object"
    ) {
      // Transform select_timestamp data from API format to frontend format
      if (value.value && typeof value.value === "object") {
        result[key] = {
          currentvalue: value.value.currentvalue,
          timestampvalue: value.value.timestampvalue,
        };
      } else {
        result[key] = value;
      }
    } else if (
      fieldType === ATTRIBUTE_TYPES.RECORD &&
      value &&
      typeof value === "object" &&
      ((value.records && Array.isArray(value.records)) || Array.isArray(value))
    ) {
      const recordsArray = value.records || value;
      result[key] = recordsArray.map((record) => ({
        object_type_id: record.object_type_id,
        record_id: record.record_id,
        record_display_name: record.record_display_name,
      }));
    } else {
      result[key] = value;
    }
  });

  return result;
};

export const normalizeOriginalDataForTransform = (
  original = {},
  properties = [],
) => {
  const normalized = { ...(original || {}) };

  if (normalized.attributes && typeof normalized.attributes === "object") {
    Object.assign(
      normalized,
      normalizeApiAttributes(normalized.attributes, properties),
    );
  }

  if (normalized.relations && typeof normalized.relations === "object") {
    Object.keys(normalized.relations).forEach((relKey) => {
      const ids = Array.isArray(normalized.relations[relKey])
        ? normalized.relations[relKey]
        : [];
      normalized[relKey] = ids;
      try {
        console.debug("[DFU] normalizeOriginalDataForTransform: relations", {
          key: relKey,
          count: ids.length,
        });
      } catch (e) {}
    });
  }

  const bySlug = (properties || []).reduce((acc, p) => {
    acc[p.slug] = p;
    return acc;
  }, {});

  Object.keys(normalized).forEach((key) => {
    const attr = bySlug[key];
    const rawValue = normalized[key];

    if (Array.isArray(rawValue)) {
      const typeLower = (attr?.type || attr?.datatype || "").toLowerCase();

      if (typeLower === ATTRIBUTE_TYPES.EMAIL) {
        const first = rawValue[0];
        normalized[key] =
          typeof first === "string" ? first : first?.email || "";
      } else if (typeLower === ATTRIBUTE_TYPES.PHONE_NUMBER) {
        normalized[key] = extractPhoneNumber(rawValue[0]);
      } else if (typeLower === ATTRIBUTE_TYPES.DOMAIN) {
        const first = rawValue[0];
        normalized[key] =
          typeof first === "string" ? first : first?.domain || "";
      }
    } else if (attr) {
      const fieldType = (attr.type || attr.datatype || "").toLowerCase();

      switch (fieldType) {
        case ATTRIBUTE_TYPES.NUMBER:
          normalized[key] =
            rawValue !== null && rawValue !== undefined
              ? Number(rawValue)
              : null;
          break;
        case ATTRIBUTE_TYPES.BOOLEAN:
          if (typeof rawValue === "boolean") {
            normalized[key] = rawValue;
          } else if (typeof rawValue === "string") {
            if (rawValue.toLowerCase() === "true" || rawValue === "Yes") {
              normalized[key] = true;
            } else if (
              rawValue.toLowerCase() === "false" ||
              rawValue === "No"
            ) {
              normalized[key] = false;
            } else {
              normalized[key] = null;
            }
          } else {
            normalized[key] = rawValue;
          }
          break;
        case ATTRIBUTE_TYPES.DATE: {
          if (rawValue !== null && rawValue !== undefined) {
            if (typeof rawValue === "number") {
              const normalizedTs = normalizeTimestamp(rawValue);
              if (normalizedTs !== null) {
                const d = dayjs(normalizedTs);
                normalized[key] = d.isValid()
                  ? d.format("YYYY-MM-DD")
                  : rawValue;
              }
            } else if (typeof rawValue === "string") {
              const d = dayjs(rawValue);
              normalized[key] = d.isValid() ? d.format("YYYY-MM-DD") : rawValue;
            } else {
              normalized[key] = rawValue;
            }
          }
          break;
        }
        case ATTRIBUTE_TYPES.DATETIME: {
          if (rawValue !== null && rawValue !== undefined) {
            // Datetime fields typically come as timestamps
            if (typeof rawValue === "number") {
              const normalizedTs = normalizeTimestamp(rawValue);
              if (normalizedTs !== null) {
                const d = dayjs(normalizedTs);
                normalized[key] = d.isValid()
                  ? d.format("YYYY-MM-DD HH:mm:ss")
                  : rawValue;
              }
            } else if (typeof rawValue === "string") {
              const d = dayjs(rawValue);
              normalized[key] = d.isValid()
                ? d.format("YYYY-MM-DD HH:mm:ss")
                : rawValue;
            } else {
              normalized[key] = rawValue;
            }
          }
          break;
        }
        default:
          break;
      }
    }
  });

  (properties || []).forEach((property) => {
    const slug = property.slug;
    const fieldType = property.type || property.datatype;
    const fieldTypeLower = String(fieldType || "").toLowerCase();

    // Normalize existing multi-select values that might be empty strings or contain empty strings
    if (
      (fieldTypeLower === ATTRIBUTE_TYPES.MULTI_SELECT ||
        fieldTypeLower === "tags") &&
      slug in normalized
    ) {
      const currentValue = normalized[slug];
      if (currentValue === "" || currentValue === null) {
        normalized[slug] = [];
      } else if (Array.isArray(currentValue)) {
        normalized[slug] = currentValue.filter(
          (v) => v !== null && v !== undefined && v !== "",
        );
      }
    }

    if (!(slug in normalized)) {
      if (fieldTypeLower === ATTRIBUTE_TYPES.BOOLEAN) {
        normalized[slug] = null;
      } else {
        switch (fieldTypeLower) {
          case ATTRIBUTE_TYPES.SELECT:
          case ATTRIBUTE_TYPES.USER:
            normalized[slug] = null;
            break;
          case ATTRIBUTE_TYPES.MULTI_SELECT:
          case ATTRIBUTE_TYPES.FILE:
          case ATTRIBUTE_TYPES.IMAGE:
          case "relationship":
            normalized[slug] = [];
            break;
          case ATTRIBUTE_TYPES.NUMBER:
            normalized[slug] = null;
            break;
          case ATTRIBUTE_TYPES.DATETIME:
          case ATTRIBUTE_TYPES.DATE:
            normalized[slug] = null;
            break;
          default:
            normalized[slug] = "";
        }
      }
    }
  });

  return normalized;
};

export const isComponentType = (property, type) => {
  const fieldType = property.type || property.datatype;
  const fieldSlug = property.slug || property.name;
  const fieldId = property.id;
  return getComponentTypeFromDatatype(fieldType, fieldSlug, fieldId) === type;
};

export const getFieldPlaceholder = (property, fieldType) => {
  if (!fieldType) return "";
  const name = property.name || property.label || "value";

  switch (fieldType.toLowerCase()) {
    case ATTRIBUTE_TYPES.USER:
      return `Select ${name}`;
    case ATTRIBUTE_TYPES.MULTI_SELECT:
      return `Select ${name}`;
    default:
      return "";
  }
};

export const getUnifiedPlaceholder = (property) => {
  const fieldType = property.type || property.datatype;
  const dynamic = getFieldPlaceholder(property, fieldType);
  if (dynamic) return dynamic;
  const fieldSlug = property.slug || property.name;
  const fieldId = property.id;
  const componentType = getComponentTypeFromDatatype(
    fieldType,
    fieldSlug,
    fieldId,
  );
  return getPlaceholderText(property, componentType);
};

export const isDynamicCRMEnabled = () => {
  try {
    return localStorage.getItem("dynamic_crm") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if dynamic crm is enabled: ",
      error,
    );
  }
};

export const isStandardObjectsV2Enabled = () => {
  try {
    return localStorage.getItem("standard_objects_v2") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if standard objects v2 is enabled: ",
      error,
    );
  }
};

export const isCustomizeLayoutEnabled = () => {
  try {
    return localStorage.getItem("customize_layout") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if customize layout is enabled: ",
      error,
    );
  }
};

export const isAliasEnabled = () => {
  try {
    return localStorage.getItem("alias") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if alias is enabled: ",
      error,
    );
  }
};

export const isABACEnabled = () => {
  try {
    return localStorage.getItem("auth_v2") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if abac is enabled: ",
      error,
    );
    return false;
  }
};

export const isGlobalSearchEnabled = () => {
  try {
    return localStorage.getItem("global_search") === "true";
  } catch (error) {
    console.error(
      "An error occurred while checking if global search is enabled: ",
      error,
    );
    return false;
  }
};

const schemaCache = new Map();

export const getObjectDisplayNames = async (slug) => {
  try {
    const defaultObject = standardObjects.find((obj) => obj.slug === slug);
    if (defaultObject) {
      return {
        singular: defaultObject.singular,
        plural: defaultObject.label,
      };
    }

    if (schemaCache.has(slug)) {
      const cachedSchema = schemaCache.get(slug);
      return {
        singular: cachedSchema.name,
        plural: cachedSchema.plural_name,
      };
    }

    const response = await CRMService.fetchSchemaObject(slug);
    if (response?.success && response?.schema?.object) {
      const objectData = response.schema.object;

      schemaCache.set(slug, objectData);

      return {
        singular: objectData.name,
        plural: objectData.plural_name,
      };
    }

    return {
      singular: capitalizeFirstLetter(slug.slice(0, -1)),
      plural: capitalizeFirstLetter(slug),
    };
  } catch (error) {
    console.error("Error fetching object display names:", error);

    return {
      singular: capitalizeFirstLetter(slug.slice(0, -1)),
      plural: capitalizeFirstLetter(slug),
    };
  }
};

export const clearSchemaCache = () => {
  schemaCache.clear();
};

const createTypeChecker = (checkFn) => (property) => {
  const fieldType = property?.type || property?.datatype;
  return fieldType ? checkFn(fieldType.toLowerCase()) : false;
};

export const isTextInput = createTypeChecker((type) =>
  [ATTRIBUTE_TYPES.TEXT, ATTRIBUTE_TYPES.EMAIL, ATTRIBUTE_TYPES.URL].includes(
    type,
  ),
);

export const isPhoneInput = createTypeChecker(
  (type) => type === ATTRIBUTE_TYPES.PHONE_NUMBER,
);

export const isNumberInput = createTypeChecker(
  (type) => type === ATTRIBUTE_TYPES.NUMBER,
);

export const isTextarea = createTypeChecker((type) => type === "textarea");

export const isSelect = createTypeChecker((type) =>
  [
    ATTRIBUTE_TYPES.SELECT,
    ATTRIBUTE_TYPES.MULTI_SELECT,
    ATTRIBUTE_TYPES.USER,
  ].includes(type),
);

export const isMultiSelect = createTypeChecker(
  (type) => type === ATTRIBUTE_TYPES.MULTI_SELECT,
);

export const isDateInput = createTypeChecker((type) =>
  [ATTRIBUTE_TYPES.DATE, ATTRIBUTE_TYPES.DATETIME].includes(type),
);

export const isBooleanInput = createTypeChecker(
  (type) => type === ATTRIBUTE_TYPES.BOOLEAN,
);

export const isTagsInput = createTypeChecker((type) => type === "tags");

export const isFileInput = createTypeChecker((type) => isFileLikeType(type));

export const isRecordInput = createTypeChecker(
  (type) => type === ATTRIBUTE_TYPES.RECORD,
);

// Type slugs that are never editable and never shown in any Create/Edit form.
const NON_EDITABLE_FIELD_TYPES = new Set([
  ATTRIBUTE_TYPES.FORMULA,
  ATTRIBUTE_TYPES.PROPERTY_SYNC,
  "select_timestamp",
  "action",
]);

export const isFieldVisibleInForm = (property, ctx = {}) => {
  if (!property) return false;

  const { isEditMode = false, isBulkCreateMode = false } = ctx;

  const fieldName = String(property.slug || property.name || "").toLowerCase();
  const fieldId = String(property.id || "").toLowerCase();
  const fieldType = String(
    property.type || property.datatype || "",
  ).toLowerCase();

  const matches = (list) =>
    list.some((x) => {
      const v = String(x).toLowerCase();
      return (fieldId && v === fieldId) || (fieldName && v === fieldName);
    });

  // 1. Always-excluded system/internal field ids & slugs
  if (matches(excludedFormFields)) return false;

  // 2. Fields hidden from Add forms only (still shown in edit/detail views)
  if (matches(excludedAddFormFields)) return false;

  // 3. Fields hidden from Create mode only (shown in Edit)
  if (!isEditMode && matches(excludedCreateOnlyFields)) return false;

  // 4. Type-based exclusions
  if (NON_EDITABLE_FIELD_TYPES.has(fieldType)) return false;

  // 5. Per-property schema flags set by the backend
  if (property.is_editable === false) return false;
  if (property.is_internal === true) return false;

  // 6. Bulk create mode hides all relationship-type fields
  if (isBulkCreateMode && fieldType === "relationship") return false;

  return true;
};

export const getOptimizedComponentConfig = (property, options = {}) => {
  const fieldSlug = property.slug || property.name || property.label;
  const fieldId = property.id;
  const componentType = getComponentTypeFromDatatype(
    property.type || property.datatype,
    fieldSlug,
    fieldId,
  );
  const fieldType = property.type || property.datatype;

  const baseConfig = {
    type: componentType,
    placeholder: getUnifiedPlaceholder(property),
    class: options.class || "flex-1",
    allowClear: true,
    ...options,
  };

  switch (componentType) {
    case ATTRIBUTE_TYPES.PHONE_NUMBER:
      return {
        ...baseConfig,
        component: "vue-tel-input",
        props: {
          defaultCountry: "US",
          dropdownOptions: {
            showFlags: true,
            showDialCodeInList: true,
            showSearchBox: true,
            searchBoxPlaceholder: "Search country..",
          },
          mode: "international",
          ...options.phoneProps,
        },
      };

    case "textarea": {
      const textareaClass = options.class
        ? `${options.class} textarea_medium`
        : "textarea_medium";
      return {
        ...baseConfig,
        component: "a-textarea",
        class: textareaClass,
        allowClear: false,
        props: {
          rows: options.rows || 4,
          ...options.textareaProps,
        },
      };
    }

    case ATTRIBUTE_TYPES.NUMBER:
      return {
        ...baseConfig,
        component: "a-input-number",
        class: options.class || "flex-1 custom-input-number",
        props: {
          min: options.min || 0,
          ...options.numberProps,
        },
      };

    case "relationship": {
      const currentSlug =
        options.currentSlug || property?.metadata?.source_object?.slug || "";

      const allowMulti = isMultiSelectAllowed(property.metadata, currentSlug);

      return {
        ...baseConfig,
        component: "a-select",
        placeholder: `Select ${property.name || property.label || ""}`,
        class: options.class || "w-full",
        props: {
          mode: allowMulti ? "multiple" : undefined,
          showSearch: true,
          filterOption: false,
          allowClear: true,
          labelInValue: true,
        },
        needsOptions: true,
        suffixIcon: true,
      };
    }

    case ATTRIBUTE_TYPES.SELECT:
    case "multiselect":
    case ATTRIBUTE_TYPES.USER:
      return {
        ...baseConfig,
        component: "a-select",
        class: options.class || "w-full",
        props: {
          mode:
            fieldType === ATTRIBUTE_TYPES.MULTI_SELECT ||
            componentType === "multiselect"
              ? "multiple"
              : undefined,
          "show-search": true,
          "filter-option": (input, option) => {
            const inputLower = input.toLowerCase();
            const label = String(option.label || "").toLowerCase();
            const value = String(option.value || "").toLowerCase();
            return label.includes(inputLower) || value.includes(inputLower);
          },
          virtual: true,
          "list-height": 320,
          "list-item-height": 32,
          ...options.selectProps,
        },
        suffixIcon: true,
        needsOptions: true,
      };

    case ATTRIBUTE_TYPES.DATE:
    case ATTRIBUTE_TYPES.DATETIME:
      return {
        ...baseConfig,
        component: "a-date-picker",
        class: options.class || "w-full",
        props: {
          showTime:
            fieldType === ATTRIBUTE_TYPES.DATETIME || fieldType === "DateTime",
          format:
            fieldType === ATTRIBUTE_TYPES.DATETIME || fieldType === "DateTime"
              ? "MM/DD/YYYY HH:mm"
              : "MM/DD/YYYY",
          valueFormat:
            fieldType === ATTRIBUTE_TYPES.DATETIME || fieldType === "DateTime"
              ? "YYYY-MM-DD HH:mm:ss"
              : "YYYY-MM-DD",
          ...options.dateProps,
        },
      };

    case ATTRIBUTE_TYPES.TIME:
      return {
        ...baseConfig,
        component: "a-time-picker",
        class: options.class || "w-full",
        props: {
          format: "HH:mm",
          valueFormat: "HH:mm",
          ...options.timeProps,
        },
      };

    case ATTRIBUTE_TYPES.BOOLEAN:
      return {
        ...baseConfig,
        component: "a-select",
        class: options.class || "w-full",
        props: {
          "show-search": true,
          "filter-option": (input, option) => {
            const inputLower = input.toLowerCase();
            const label = String(option.label || "").toLowerCase();
            const value = String(option.value || "").toLowerCase();
            return label.includes(inputLower) || value.includes(inputLower);
          },
        },
        options: [
          { label: "Yes", value: true },
          { label: "No", value: false },
        ],
        suffixIcon: true,
      };

    case "tags":
      return {
        ...baseConfig,
        component: "a-select",
        class: options.class || "w-full",
        props: {
          mode: "tags",
          "show-search": true,
          "filter-option": (input, option) => {
            const inputLower = input.toLowerCase();
            const label = String(option.label || "").toLowerCase();
            const value = String(option.value || "").toLowerCase();
            return label.includes(inputLower) || value.includes(inputLower);
          },
          tokenSeparators: [","],
          ...options.selectProps,
        },
        suffixIcon: true,
        needsOptions: true,
      };

    case ATTRIBUTE_TYPES.FILE:
    case ATTRIBUTE_TYPES.IMAGE:
    case "file":
    case "image":
      // File/image inputs should be handled by CrmFileUploadField component
      // If we reach here, return a hidden input as fallback
      return {
        ...baseConfig,
        component: "a-input",
        class: "hidden",
        props: {
          type: "text",
          disabled: true,
        },
      };

    default:
      return {
        ...baseConfig,
        component: "a-input",
        class: "input_medium",
        props: {
          type: getInputType(property),
          ...options.inputProps,
        },
      };
  }
};

export const getInputType = (property) => {
  const fieldType = property.type || property.datatype;
  switch (fieldType) {
    case ATTRIBUTE_TYPES.URL:
    case "URL":
      return "url";
    case ATTRIBUTE_TYPES.EMAIL:
    case "Email":
      return "email";
    case ATTRIBUTE_TYPES.FILE:
    case ATTRIBUTE_TYPES.IMAGE:
    case "file":
    case "image":
      // File/image inputs should never reach here, but prevent type="file"
      return "text";
    default:
      return "text";
  }
};

export const getFieldKey = (property) => property.slug;

export const getFieldLabel = (property) => {
  const label = property.name || property.label || property.slug || "Field";
  return label;
};

export const needsFieldOptions = (property) => {
  const fieldType = (property.type || property.datatype || "").toLowerCase();
  const idLower = String(property.id || "").toLowerCase();
  const slugLower = String(property.slug || property.name || "").toLowerCase();

  // Pipeline/stage fields — use schema structured_options when available
  if (
    PIPELINE_FIELD_IDS.includes(idLower) ||
    PIPELINE_FIELD_IDS.includes(slugLower)
  ) {
    return !(
      property.structured_options?.length > 0 || property.options?.length > 0
    );
  }

  if (fieldType === ATTRIBUTE_TYPES.USER) {
    return true;
  }

  if (fieldType === "relationship") {
    return true;
  }

  // Tags fields - use schema options when available, else fetch from API
  if (fieldType === "tags" || idLower === "tags" || slugLower === "tags") {
    if (
      property.options &&
      Array.isArray(property.options) &&
      property.options.length > 0
    ) {
      return false;
    }
    return true;
  }

  // Check for country fields regardless of type (they should always have options)
  if (
    idLower === "country" ||
    idLower === "countries" ||
    slugLower === "country" ||
    slugLower === "countries"
  ) {
    return true;
  }

  if (
    (fieldType === ATTRIBUTE_TYPES.SELECT ||
      fieldType === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    (idLower === "country" ||
      idLower === "countries" ||
      slugLower === "country" ||
      slugLower === "countries")
  ) {
    return true;
  }

  if (
    (fieldType === ATTRIBUTE_TYPES.SELECT ||
      fieldType === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    (idLower === "current_company" ||
      idLower === "company" ||
      idLower === "current_company_id" ||
      slugLower === "current_company" ||
      slugLower === "company" ||
      slugLower === "current_company_id")
  ) {
    return true;
  }

  // Lead source field - always fetch from API
  if (idLower === "source") {
    return true;
  }

  // For other select fields, check if they already have options
  if (
    (fieldType === ATTRIBUTE_TYPES.SELECT ||
      fieldType === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    property.options &&
    Array.isArray(property.options) &&
    property.options.length > 0
  ) {
    return false; // Already has options, no need to fetch
  }

  return false;
};

export const isCountrySelect = (property) => {
  const typeLower = String(
    property.type || property.datatype || "",
  ).toLowerCase();
  const idLower = String(property.id || "").toLowerCase();
  const slugLower = String(property.slug || property.name || "").toLowerCase();
  return (
    (typeLower === ATTRIBUTE_TYPES.SELECT ||
      typeLower === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    (idLower === "country" ||
      idLower === "countries" ||
      slugLower === "country" ||
      slugLower === "countries")
  );
};

export const shouldUseApiSearch = (property) => {
  const fieldType = (property.type || property.datatype || "").toLowerCase();
  const idLower = String(property.id || "").toLowerCase();
  const slugLower = String(property.slug || property.name || "").toLowerCase();

  if (fieldType === ATTRIBUTE_TYPES.USER) {
    return false;
  }

  if (
    (fieldType === ATTRIBUTE_TYPES.SELECT ||
      fieldType === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    (idLower === "country" ||
      idLower === "countries" ||
      slugLower === "country" ||
      slugLower === "countries")
  ) {
    return false;
  }

  return needsFieldOptions(property) && fieldType === "relationship";
};

export const getFieldOptionsFromProperty = (property, fieldOptions = {}) => {
  const fieldKey = getFieldKey(property);
  const fieldType = (property.type || property.datatype || "").toLowerCase();

  if (fieldOptions[fieldKey] && Array.isArray(fieldOptions[fieldKey])) {
    return fieldOptions[fieldKey];
  }

  // Pipeline/stage fields — prefer structured_options which contain id-based values
  const idLower = String(property.id || "").toLowerCase();
  const slugLower = String(property.slug || property.name || "").toLowerCase();
  if (
    PIPELINE_FIELD_IDS.includes(idLower) ||
    PIPELINE_FIELD_IDS.includes(slugLower)
  ) {
    const raw =
      property.structured_options?.length > 0
        ? property.structured_options
        : property.options?.length > 0
          ? property.options
          : [];
    return toFormOptions(raw).map((opt) => ({
      ...opt,
      value: Number(opt.value) || opt.value,
    }));
  }

  if (
    property.options &&
    Array.isArray(property.options) &&
    property.options.length > 0
  ) {
    const formatted = property.options.map((option) => {
      if (typeof option === "string") {
        return { label: option, value: option };
      }
      if (
        option &&
        typeof option === "object" &&
        option.label !== undefined &&
        option.value !== undefined
      ) {
        return option;
      }
      if (option && typeof option === "object" && option.option !== undefined) {
        return { label: option.option, value: option.option };
      }
      // Handle options with id and name (like pipeline, pipeline_stage_id)
      if (
        option &&
        typeof option === "object" &&
        option.id !== undefined &&
        option.name !== undefined
      ) {
        return { label: option.name, value: option.id };
      }
      return { label: String(option), value: String(option) };
    });

    return formatted;
  }

  if (fieldType === ATTRIBUTE_TYPES.BOOLEAN) {
    return [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ];
  }

  return [];
};

export const getFieldRules = (property) => {
  const rules = [];
  const fieldName = property.name || property.label || "Field";

  if (property.required || property.is_required) {
    rules.push({
      required: true,
      message: `${fieldName} is required!`,
    });
  }

  const fieldType = property.type || property.datatype;
  const fieldTypeLower = String(fieldType || "").toLowerCase();

  switch (fieldTypeLower) {
    case ATTRIBUTE_TYPES.URL:
      rules.push({
        pattern:
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
        message: "Please enter a valid URL format (e.g., https://example.com)",
        trigger: ["blur"],
      });
      break;

    case ATTRIBUTE_TYPES.EMAIL:
      rules.push({
        pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email format (e.g., user@example.com)",
        trigger: ["blur"],
      });
      break;

    case ATTRIBUTE_TYPES.DOMAIN:
      rules.push({
        validator: (_, value) => {
          if (value === undefined || value === null || value === "") {
            return Promise.resolve();
          }
          return isDomainValid(String(value))
            ? Promise.resolve()
            : Promise.reject(
                new Error("Please enter a valid domain (e.g., example.com)"),
              );
        },
        trigger: ["blur"],
      });
      break;

    case ATTRIBUTE_TYPES.NUMBER:
      break;
  }

  return rules;
};

export const getFieldDefaultValue = (property) => {
  const fieldType = (property.type || property.datatype || "").toLowerCase();
  const fieldSlug = property.slug || property.name;
  const fieldId = property.id;

  // Use component type to handle fields like country that have type "text" but render as "select"
  const componentType = getComponentTypeFromDatatype(
    fieldType,
    fieldSlug,
    fieldId,
  );

  switch (componentType) {
    case ATTRIBUTE_TYPES.BOOLEAN:
      return null;
    case ATTRIBUTE_TYPES.MULTI_SELECT:
    case "multiselect":
    case "tags":
      return [];
    case ATTRIBUTE_TYPES.FILE:
    case ATTRIBUTE_TYPES.IMAGE:
      return [];
    case ATTRIBUTE_TYPES.SELECT:
    case ATTRIBUTE_TYPES.USER:
      return null;
    case ATTRIBUTE_TYPES.NUMBER:
      return null;
    case ATTRIBUTE_TYPES.DATE:
    case ATTRIBUTE_TYPES.DATETIME:
    case ATTRIBUTE_TYPES.TIME:
      return null;
    default:
      return "";
  }
};

export const normalizeArrayFieldValue = (value, property) => {
  if (!Array.isArray(value)) return value;

  const fieldType = (property?.type || property?.datatype || "").toLowerCase();
  const fieldSlug = (property?.slug || "").toLowerCase();

  if (fieldType === ATTRIBUTE_TYPES.EMAIL || fieldSlug.includes("email")) {
    const first = value[0];
    return typeof first === "string" ? first : first?.email || "";
  }

  if (fieldType === ATTRIBUTE_TYPES.MULTI_SELECT || fieldType === "tags") {
    return value.filter((v) => v !== null && v !== undefined && v !== "");
  }

  return value;
};

export const getRelationshipBetweenEntities = (
  schema,
  sourceSlug,
  targetSlug,
) => {
  if (!schema?.relationships) return null;

  return schema.relationships.find((rel) => {
    const relSourceSlug = rel.source_object?.slug || rel.source_object_id;
    const relTargetSlug = rel.target_object?.slug || rel.target_object_id;

    return (
      (relSourceSlug === sourceSlug && relTargetSlug === targetSlug) ||
      (relSourceSlug === targetSlug && relTargetSlug === sourceSlug)
    );
  });
};

export const isMultiSelectAllowed = (relationship, currentSlug) => {
  if (!relationship?.cardinality) return true;

  const {
    cardinality,
    source_object_id,
    target_object_id,
    source_object,
    target_object,
  } = relationship;

  let isCurrentEntitySource = false;

  if (source_object?.slug) {
    isCurrentEntitySource = source_object.slug === currentSlug;
  } else if (target_object?.slug) {
    isCurrentEntitySource = target_object.slug !== currentSlug;
  } else {
    isCurrentEntitySource = source_object_id === currentSlug;
  }

  const [sourceCardinality, targetCardinality] = cardinality.split("_to_");

  if (isCurrentEntitySource) {
    return targetCardinality === "many";
  } else {
    return sourceCardinality === "many";
  }
};

export const parseRelationRecords = (value) => {
  try {
    if (!value) return [];

    let parsed = value;
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (parsed && Array.isArray(parsed.records)) {
      return parsed.records;
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    return [];
  } catch (error) {
    console.error("Error parsing relation records:", error);
    return [];
  }
};

export const getRecordUrl = (record, store) => {
  if (!record) return "#";
  const obj = store.getters["objects/getObjectBySlugOrId"](
    record.object_type_id,
  );
  const objectSlug = obj?.slug || record.object_type_id;
  return `/${objectSlug}/${record.record_id}`;
};

export const getInitial = (name) => {
  if (!name) return "?";
  return name.charAt(0).toUpperCase();
};

const avatarColors = [
  "#9A2A2A",
  "#2A5A9A",
  "#2A9A5A",
  "#9A5A2A",
  "#5A2A9A",
  "#2A9A9A",
  "#9A2A5A",
  "#5A9A2A",
  "#2A2A9A",
  "#9A9A2A",
];

export const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  const charCode = name.charCodeAt(0);
  return avatarColors[charCode % avatarColors.length];
};

export const isRelationRecordValue = (value) => {
  try {
    if (!value) return false;

    let parsedValue = value;
    if (typeof value === "string") {
      try {
        parsedValue = JSON.parse(value);
      } catch {
        return false;
      }
    }

    if (
      parsedValue &&
      typeof parsedValue === "object" &&
      !Array.isArray(parsedValue)
    ) {
      if (parsedValue.records && Array.isArray(parsedValue.records))
        return true;
      if (parsedValue.record_display_name) return true;
    }

    if (Array.isArray(parsedValue)) {
      if (parsedValue.length > 0 && parsedValue[0]?.record_display_name)
        return true;
    }

    return false;
  } catch {
    return false;
  }
};

export const isRelationRecordType = (attribute) => {
  if (!attribute) return false;
  const type = attribute.dataType || attribute.type || attribute.attributeType;
  return type === attributeTypes.RELATION_RECORD || type === "relation_record";
};

const RELATION_KEY_MAP = {
  [entityTypes.COMPANIES]: "company_ids",
  [entityTypes.CONTACTS]: "contact_ids",
  [entityTypes.LEADS]: "lead_ids",
  [entityTypes.DEALS]: "deal_ids",
};

export function convertRelationsToFlatFormat(relations) {
  const flatRelations = {};

  if (!relations || typeof relations !== "object") {
    return flatRelations;
  }

  for (const [relKey, relValue] of Object.entries(relations)) {
    const flatKey = RELATION_KEY_MAP[relKey];
    if (flatKey && relValue && Array.isArray(relValue.ids)) {
      flatRelations[flatKey] = relValue.ids;
    } else if (flatKey && Array.isArray(relValue)) {
      flatRelations[flatKey] = relValue;
    }
  }

  return flatRelations;
}

export function separateStandardAndCustomProperties(
  attributes,
  properties = [],
) {
  const standardAttributes = {};
  const customProperties = {};

  if (!attributes || typeof attributes !== "object") {
    return { standardAttributes, customProperties };
  }

  const customPropertySlugs = new Set();
  for (const prop of properties) {
    const propId = prop.id || prop.slug;
    if (isUUID(propId)) {
      customPropertySlugs.add(prop.slug || propId);
    }
  }

  for (const [key, value] of Object.entries(attributes)) {
    if (customPropertySlugs.has(key) || isUUID(key)) {
      customProperties[key] = value;
    } else {
      standardAttributes[key] = value;
    }
  }

  return { standardAttributes, customProperties };
}

export function transformToStandardEntityPayload(entityData, properties = []) {
  const { standardAttributes, customProperties } =
    separateStandardAndCustomProperties(entityData.attributes, properties);

  const flatRelations = convertRelationsToFlatFormat(entityData.relations);

  const payload = {
    ...standardAttributes,
    ...flatRelations,
  };

  if (entityData.parent_id !== undefined) {
    payload.parent_id = entityData.parent_id;
  }

  if (Object.keys(customProperties).length > 0) {
    payload.custom_properties = customProperties;
  }

  return payload;
}

export const STANDARD_ENTITY_ENDPOINTS = {
  [entityTypes.CONTACTS]: "/api/contacts",
  [entityTypes.LEADS]: "/api/leads",
  [entityTypes.COMPANIES]: "/api/companies",
  [entityTypes.DEALS]: "/api/deals",
};

export function isStandardEntity(slug) {
  return slug in STANDARD_ENTITY_ENDPOINTS;
}

export function getStandardEntityEndpoint(slug, entityId = null) {
  const baseEndpoint = STANDARD_ENTITY_ENDPOINTS[slug];
  if (!baseEndpoint) return null;
  return entityId ? `${baseEndpoint}/${entityId}` : baseEndpoint;
}

export async function patchStandardEntity(
  CRMServiceInstance,
  slug,
  entityId,
  payload,
  service = null,
) {
  try {
    const patchMethodMap = {
      [entityTypes.CONTACTS]: () =>
        CRMServiceInstance.patchContact(entityId, payload),
      [entityTypes.LEADS]: () =>
        CRMServiceInstance.patchLead(entityId, payload),
      [entityTypes.COMPANIES]: () =>
        CRMServiceInstance.patchCompany(entityId, payload),
      [entityTypes.DEALS]: () =>
        CRMServiceInstance.patchDeal(entityId, payload),
    };

    const patchMethod = patchMethodMap[slug];
    if (!patchMethod) {
      return CRMServiceInstance.updateDynamicEntity(
        slug,
        entityId,
        payload,
        service,
      );
    }

    return patchMethod();
  } catch (error) {
    console.error("An error occurred while patching standard entity: ", error);
    throw error;
  }
}

export async function createStandardEntity(
  CRMServiceInstance,
  slug,
  payload,
  properties = [],
  service = null,
) {
  try {
    const isStandard = isStandardEntity(slug);
    const flatPayload = isStandard
      ? transformToStandardEntityPayload(payload, properties)
      : payload;

    const postMethodMap = {
      [entityTypes.CONTACTS]: () => CRMServiceInstance.postContact(flatPayload),
      [entityTypes.LEADS]: () => CRMServiceInstance.postLead(flatPayload),
      [entityTypes.COMPANIES]: () =>
        CRMServiceInstance.postCompany(flatPayload),
      [entityTypes.DEALS]: () => CRMServiceInstance.postDeal(flatPayload),
    };

    const postMethod = postMethodMap[slug];
    if (!postMethod) {
      return CRMServiceInstance.createDynamicEntity(slug, payload, service);
    }

    return postMethod();
  } catch (error) {
    console.error("An error occurred while creating standard entity: ", error);
    throw error;
  }
}

export const fetchEntityLabelsForIds = async (entitySlug, ids) => {
  if (!entitySlug || !ids || ids.length === 0) return [];

  const slugLower = entitySlug.toLowerCase();

  // Fetch primary attribute slug to handle renamed properties
  let primaryAttrSlug = null;
  try {
    primaryAttrSlug = await getPrimaryAttributeSlug(entitySlug);
  } catch (error) {
    console.warn(`Failed to fetch primary attribute for ${entitySlug}:`, error);
  }

  const extractByPrimaryAttr = (rec) => {
    if (!primaryAttrSlug) return null;
    // Flat format (standard objects)
    const flatVal = rec?.[primaryAttrSlug];
    if (flatVal && typeof flatVal === "string" && flatVal.trim())
      return flatVal;
    // Nested format (custom/dynamic objects)
    const attrs = rec?.attributes || {};
    const displayValue = getDisplayValueFromPrimaryAttribute(
      attrs,
      primaryAttrSlug,
      null,
    );
    return displayValue || null;
  };

  const labelExtractors = {
    [entityTypes.CONTACTS]: (rec) =>
      rec?.full_name || rec?.name || rec?.email || String(rec?.id),
    [entityTypes.COMPANIES]: (rec) =>
      rec?.company_name || rec?.name || String(rec?.id),
    [entityTypes.DEALS]: (rec) => rec?.title || rec?.name || String(rec?.id),
    [entityTypes.LEADS]: (rec) =>
      rec?.full_name || rec?.name || rec?.email || String(rec?.id),
    default: (rec) => {
      const attrs = rec?.attributes || {};
      return attrs?.name?.value || attrs?.title?.value || String(rec?.id);
    },
  };

  // Helper to extract record from response (handles both direct and wrapped responses)
  const extractRecord = (response, entityKey) => {
    if (!response) return null;
    if (response.success && response[entityKey]) {
      return response[entityKey];
    }
    if (response[entityKey]) {
      return response[entityKey];
    }
    if (response.id) {
      return response;
    }
    return null;
  };

  try {
    const results = [];
    for (const id of ids) {
      try {
        let response;
        let record = null;

        if (slugLower === entityTypes.CONTACTS) {
          response = await DashboardService.getContact(id);
          record = extractRecord(response, "contact");
        } else if (slugLower === entityTypes.COMPANIES) {
          response = await DashboardService.getCompany(id);
          record = extractRecord(response, "company");
        } else if (slugLower === entityTypes.DEALS) {
          response = await DashboardService.fetchDealDetails(id);
          record = extractRecord(response, "deal");
        } else if (slugLower === entityTypes.LEADS) {
          response = await DashboardService.getLead(id);
          record = extractRecord(response, "lead");
        } else {
          response = await CRMService.fetchDynamicEntityDetails(slugLower, id);
          record = response?.success
            ? response.record
            : extractRecord(response, "record");
        }

        if (record) {
          const fallbackExtractor =
            labelExtractors[slugLower] || labelExtractors.default;
          const label =
            extractByPrimaryAttr(record) || fallbackExtractor(record);
          results.push({
            label: label || String(id),
            value: id,
          });
        } else {
          results.push({ label: String(id), value: id });
        }
      } catch (err) {
        results.push({ label: String(id), value: id });
      }
    }
    return results;
  } catch (error) {
    return ids.map((id) => ({ label: String(id), value: id }));
  }
};

export const fetchDealRelationships = async (dealId) => {
  const relations = {
    companies: [],
    contacts: [],
    leads: [],
  };

  if (!dealId) return relations;

  try {
    const [companiesRes, contactsRes, leadsRes] = await Promise.all([
      DashboardService.getCompaniesByDealID(dealId).catch(() => null),
      DashboardService.fetchDealContacts(dealId).catch(() => null),
      DashboardService.getLeadsByDealID(dealId).catch(() => null),
    ]);

    if (companiesRes?.success && Array.isArray(companiesRes.companies)) {
      relations.companies = companiesRes.companies.map((c) => c.id);
    }
    if (contactsRes?.success && Array.isArray(contactsRes.contacts)) {
      relations.contacts = contactsRes.contacts.map((c) => c.id);
    }
    if (leadsRes?.success && Array.isArray(leadsRes.leads)) {
      relations.leads = leadsRes.leads.map((l) => l.id);
    }
  } catch (error) {
    console.error(
      "[fetchDealRelationships] Error fetching deal relationships:",
      error,
    );
  }

  return relations;
};
